import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { SubSink } from 'subsink';
import { Apollo } from 'apollo-angular';
import { NbWindowRef, NbToastrService, NbDialogService } from '@nebular/theme';
import { UPDATE_EQUIPMENT_COUNTING_JOURNAL_LINE } from '../../../../@core/graphql/mutations/equipment/updateEquipmentCountingJournalLine.mutation';
import { MUTATION_UPDATEEQUIPMENT } from '../../../../@core/graphql/mutations/equipment/updateequipment.mutation';
import { Mutation_UpdateEquipmentStation } from '../../../../@core/graphql/mutations/equipment/updateEquipmentStation.mutation';
import { EquipmentIssueDialogComponent } from '../equipment-issue-dialog/equipment-issue-dialog.component';
import { WindowService } from '../../../../@core/services/window.service';
import { EquipmentDetailComponent } from '../../../equipment/detail/equipmentdetail.component';

export interface JournalLineData {
  id: string;
  journalId: string;
  equipmentId: string;
  equipmentNum?: string;
  serial?: string;
  fleetCode?: string;
  model?: string;
  make?: string;
  parentId?: string;
  fleetType?: string;
  status?: string;
  rentalStatus?: string;
  branchNum?: string;
  description?: string;
  description2?: string;
  expectedLocationNum?: string;
  expectedStation?: string;
  actualLocationNum?: string;
  actualStation?: string;
  disposition?: string;
  notes?: string;
  equipment?: {
    id?: string;
    equipmentNum?: string;
    serial?: string;
    fleetCode?: string;
    model?: string;
    make?: string;
    parentId?: string;
    description2?: string;
    fleetType?: string;
    status?: string;
    rentalContractStatus?: string;
    rentalContractNum?: string;
    branchNum?: string;
    bookValue?: number;
    description?: string;
    var1?: string;
    locationNum?: string;
    station?: string;
    issueReported?: string;
    issueNotes?: string;
    parentRef?: {
      equipmentNum?: string;
      id?: string;
    };
    customer?: {
      customerNum?: string;
      name?: string;
    };
  };
}

export interface StatusOption {
  value: string;
  label: string;
}

export interface DispositionOption {
  value: string;
  label: string;
  requiredAttachments?: string[];
}

@Component({
    selector: 'stack-mono-journal-line-detail',
    templateUrl: './journal-line-detail.component.html',
    styleUrls: ['./journal-line-detail.component.scss'],
    standalone: false
})
export class JournalLineDetailComponent implements OnInit, OnDestroy {
  _lineData!: JournalLineData;

  @Input() set lineData(value: JournalLineData) {
    // Deep copy to avoid mutating read-only objects (e.g., from Apollo cache)
    this._lineData = {
      ...value,
      equipment: value.equipment ? { ...value.equipment } : undefined,
    };
  }
  get lineData(): JournalLineData {
    return this._lineData;
  }

  private sub = new SubSink();

  // Disposition options with required attachments
  // Note: 'Not in System' is only set by workflow and should not be selectable by users
  private allDispositionOptions: DispositionOption[] = [
    {
      value: 'Lease Return Sold',
      label: 'Lease Return Sold',
      requiredAttachments: [],
    },
    { value: 'Missing', label: 'Missing', requiredAttachments: [] },
    { value: 'Not in System', label: 'Not in System', requiredAttachments: [] },
    {
      value: 'Rental on Contract',
      label: 'Rental on Contract',
      requiredAttachments: [],
    },
    {
      value: 'Research in Process',
      label: 'Research in Process',
      requiredAttachments: [],
    },
    { value: 'Scrapped', label: 'Scrapped', requiredAttachments: [] },
    {
      value: 'Shipped to Customer',
      label: 'Shipped to Customer',
      requiredAttachments: [],
    },
    {
      value: 'Ship Out / Other Dealer',
      label: 'Ship Out / Other Dealer',
      requiredAttachments: [],
    },
    {
      value: 'Stock Incoming',
      label: 'Stock Incoming',
      requiredAttachments: [],
    },
    {
      value: 'On Demo',
      label: 'On Demo',
      requiredAttachments: [],
    },

    { value: 'Other - Misc.', label: 'Other - Misc.', requiredAttachments: [] },
  ];

  // Filtered disposition options - 'Not in System' is only shown when it's the current value (set by workflow)
  get dispositionOptions(): DispositionOption[] {
    // If current disposition is 'Not in System', include it so it displays correctly
    if (this.selectedDisposition === 'Not in System') {
      return this.allDispositionOptions;
    }
    // Otherwise, filter it out so users can't select it
    return this.allDispositionOptions.filter(
      (opt) => opt.value !== 'Not in System',
    );
  }

  /**
   * Check if the disposition can be cleared
   * Disposition can be cleared when it has a value and is not 'Not in System' (workflow-only)
   */
  get canClearDisposition(): boolean {
    return (
      !!this.selectedDisposition && this.selectedDisposition !== 'Not in System'
    );
  }

  /**
   * Clear the disposition selection
   */
  clearDisposition(): void {
    this.selectedDisposition = '';
    this.updateRequiredAttachments();
  }

  /**
   * Check if the disposition field should be disabled
   * Disposition is disabled when it's set to 'Not in System' (workflow-only value)
   */
  get isDispositionDisabled(): boolean {
    return this.selectedDisposition === 'Not in System';
  }

  // Status options - 'Added' and 'Other Location' are workflow-only statuses
  private allStatusOptions: StatusOption[] = [
    { value: 'Waiting Count', label: 'Waiting Count' },
    { value: 'Counted', label: 'Counted' },
    { value: 'Added', label: 'Added' },
    { value: 'Other Location', label: 'Other Location' },
    { value: 'Not Found', label: 'Not Found' },
    { value: 'Missing', label: 'Missing' },
  ];

  // Filtered status options - 'Added' and 'Other Location' are only shown when they're the current value (set by workflow)
  get statusOptions(): StatusOption[] {
    const workflowOnlyStatuses = ['Added', 'Other Location'];
    // If current status is a workflow-only status, include it so it displays correctly
    if (workflowOnlyStatuses.includes(this.selectedStatus)) {
      return this.allStatusOptions;
    }
    // Otherwise, filter out workflow-only statuses so users can't select them
    return this.allStatusOptions.filter(
      (opt) => !workflowOnlyStatuses.includes(opt.value),
    );
  }

  /**
   * Check if the status field should be disabled
   * Status is disabled when it's set to 'Added' or 'Other Location' (workflow-only values)
   */
  get isStatusDisabled(): boolean {
    return ['Added', 'Other Location'].includes(this.selectedStatus);
  }

  documentTypes = ['Haul Ticket', 'Equipment Image', 'Other'];

  selectedDisposition = '';
  selectedStatus = '';
  notes = '';
  actualLocation = '';
  actualStation = '';
  selectedStation: any = null;
  selectedActualStation: any = null;
  reportIssue = '';

  // Attachment tracking
  hasRequiredAttachments = true;
  requiredFileTypes: string[] = [];

  isSubmitting = false;

  constructor(
    private apollo: Apollo,
    private windowRef: NbWindowRef,
    private toastrService: NbToastrService,
    private dialogService: NbDialogService,
    private windowService: WindowService,
  ) {}

  ngOnInit(): void {
    if (this.lineData) {
      this.selectedDisposition = this.lineData.disposition || '';
      this.selectedStatus = this.lineData.status || 'Waiting';
      this.notes = this.lineData.notes || '';
      this.actualLocation = this.lineData.actualLocationNum || '';
      this.actualStation = this.lineData.actualStation || '';
      if (this.actualStation) {
        this.selectedActualStation = { station: this.actualStation };
      }
      this.updateRequiredAttachments();
    }
  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  get equipmentNum(): string {
    return (
      this.lineData?.equipment?.equipmentNum ||
      this.lineData?.equipmentNum ||
      ''
    );
  }

  get parentObjectNum(): string {
    return this.lineData?.equipment?.parentRef?.equipmentNum || '';
  }

  get serial(): string {
    return this.lineData?.equipment?.serial || this.lineData?.serial || '';
  }

  get fleetCode(): string {
    return (
      this.lineData?.equipment?.fleetCode || this.lineData?.fleetCode || ''
    );
  }

  get model(): string {
    return this.lineData?.equipment?.model || this.lineData?.model || '';
  }

  get make(): string {
    return this.lineData?.equipment?.make || this.lineData?.make || '';
  }

  get parentObject(): string {
    return this.lineData?.equipment?.parentId || this.lineData?.parentId || '';
  }

  get fleetType(): string {
    return (
      this.lineData?.equipment?.fleetType || this.lineData?.fleetType || ''
    );
  }

  get equipmentStatus(): string {
    return this.lineData?.equipment?.status || this.lineData?.status || '';
  }

  get rentalStatus(): string {
    return (
      this.lineData?.equipment?.rentalContractStatus ||
      this.lineData?.rentalStatus ||
      ''
    );
  }

  get rentalContractNum(): string {
    return this.lineData?.equipment?.rentalContractNum || '';
  }

  get branch(): string {
    return (
      this.lineData?.equipment?.branchNum || this.lineData?.branchNum || ''
    );
  }

  get description(): string {
    return this.lineData?.equipment?.description || '';
  }

  get description2(): string {
    return this.lineData?.equipment?.description2 || '';
  }

  get lineStatus(): string {
    return this.selectedStatus || this.lineData?.status || 'Waiting';
  }

  get expectedLocation(): string {
    return this.lineData?.expectedLocationNum || '';
  }

  get expectedStation(): string {
    return this.lineData?.expectedStation || '';
  }

  get equipmentLocation(): string {
    return this.lineData?.equipment?.locationNum || '';
  }

  get equipmentStation(): string {
    return this.lineData?.equipment?.station || '';
  }

  get customerNum(): string {
    return this.lineData?.equipment?.customer?.customerNum || '';
  }

  get customerName(): string {
    return this.lineData?.equipment?.customer?.name || '';
  }

  get bookValue(): string {
    const value =
      this.lineData?.equipment?.bookValue ||
      (this.lineData?.equipment?.bookValue === 0 ? 0 : undefined);
    return value !== undefined ? `$${value.toFixed(2)}` : '';
  }

  get currentDispositionOption(): DispositionOption | undefined {
    return this.dispositionOptions.find(
      (opt) => opt.value === this.selectedDisposition,
    );
  }

  onDispositionChange(value: string): void {
    this.selectedDisposition = value;
    this.updateRequiredAttachments();
  }

  onStatusChange(value: string): void {
    this.selectedStatus = value;
  }

  onStationChange(stationObj: any): void {
    this.selectedStation = stationObj;
  }

  onActualStationChange(stationObj: any): void {
    this.selectedActualStation = stationObj;
    this.actualStation = stationObj?.station || '';

    // Update actual location based on the station's location
    if (stationObj?.locationRef?.locationNum) {
      this.actualLocation = stationObj.locationRef.locationNum;
    } else if (stationObj?.locationId) {
      // If locationRef is not populated but locationId exists, use it as fallback
      this.actualLocation = stationObj.locationId;
    }
  }

  updateRequiredAttachments(): void {
    const option = this.currentDispositionOption;
    this.requiredFileTypes = option?.requiredAttachments || [];
  }

  onHasRequiredFilesChange(hasFiles: boolean): void {
    this.hasRequiredAttachments = hasFiles;
  }

  canSubmit(): boolean {
    // For statuses that require Found Station/Location, check if it's selected
    if (this.isFoundStationRequired) {
      const foundStationSelected = !!this.actualStation;
      return foundStationSelected && !this.isSubmitting;
    }

    // For all other statuses (including 'Not Found'), disposition is not required
    return !this.isSubmitting;
  }

  /**
   * Check if Found Station/Location is required based on the selected status
   * Required for: Other Location, Counted, Added
   */
  get isFoundStationRequired(): boolean {
    return ['Other Location', 'Counted', 'Added'].includes(this.selectedStatus);
  }

  /**
   * Check if Disposition is required based on the selected status
   * Disposition is optional for all statuses
   */
  get isDispositionRequired(): boolean {
    return false;
  }

  /**
   * Check if the station was changed from the original equipment station
   */
  private wasStationChanged(): boolean {
    const originalStation = this.lineData?.equipment?.station || '';
    return this.actualStation !== originalStation && !!this.actualStation;
  }

  /**
   * Update the equipment station after journal line is counted
   */
  private updateEquipmentStation(): void {
    const equipmentId =
      this.lineData?.equipment?.id || this.lineData?.equipmentId;

    if (!equipmentId || !this.actualStation) {
      return;
    }

    this.sub.sink = this.apollo
      .mutate({
        mutation: Mutation_UpdateEquipmentStation,
        variables: {
          dto: {
            id: equipmentId,
            station: this.actualStation,
          },
        },
      })
      .subscribe({
        next: () => {
          // Station updated successfully - no need to notify user separately
        },
        error: (error) => {
          console.error('Error updating equipment station:', error);
          // Don't show error to user since the main journal line update succeeded
        },
      });
  }

  submit(): void {
    if (!this.canSubmit()) {
      return;
    }

    this.isSubmitting = true;

    const dto: any = {
      id: this.lineData.id,
      status: this.selectedStatus,
      disposition: this.selectedDisposition || null,
      notes: this.notes || null,
      actualLocationNum: this.actualLocation || null,
      actualStation: this.actualStation || null,
    };

    // If status is Counted, set actualQty to 1 (equipment is unique)
    if (this.selectedStatus === 'Counted') {
      dto.actualQty = 1;
    }

    // Track if station was changed before submitting
    const stationChanged = this.wasStationChanged();

    this.sub.sink = this.apollo
      .mutate({
        mutation: UPDATE_EQUIPMENT_COUNTING_JOURNAL_LINE,
        variables: { dto },
      })
      .subscribe({
        next: (result) => {
          this.isSubmitting = false;

          // If station was changed, update the equipment's station
          if (stationChanged) {
            this.updateEquipmentStation();
          }

          this.toastrService.success(
            'Journal line updated successfully',
            'Success',
          );
          // Close window with refresh flag
          this.windowRef.close({ refresh: true });
        },
        error: (error) => {
          this.isSubmitting = false;
          console.error('Error updating journal line:', error);
          this.toastrService.danger(
            error.message || 'Failed to update journal line',
            'Error',
          );
        },
      });
  }

  close(): void {
    this.windowRef.close();
  }

  /**
   * Open equipment detail in a window
   */
  openEquipmentDetail(): void {
    const equipmentId =
      this.lineData?.equipmentId || this.lineData?.equipment?.id;
    if (!equipmentId) {
      this.toastrService.warning('Equipment ID not found', 'Warning');
      return;
    }
    this.windowService.openWindow(EquipmentDetailComponent, {
      objectId: equipmentId,
      context: { id: equipmentId },
    });
  }

  openParentEquipmentDetail(): void {
    const equipmentId = this.lineData?.equipment?.parentRef?.id;
    if (!equipmentId) {
      this.toastrService.warning('Equipment ID not found', 'Warning');
      return;
    }
    this.windowService.openWindow(EquipmentDetailComponent, {
      objectId: equipmentId,
      context: { id: equipmentId },
    });
  }

  /**
   * Check if there are any issues reported
   */
  get hasIssues(): boolean {
    return !!(
      this.lineData?.equipment?.issueReported ||
      this.lineData?.equipment?.issueNotes
    );
  }

  /**
   * Open the report issue dialog
   */
  openReportIssueDialog(): void {
    const dialogRef = this.dialogService.open(EquipmentIssueDialogComponent, {
      context: {
        equipmentNum: this.equipmentNum,
        selectedIssues: this.lineData?.equipment?.issueReported || '',
        issueNotes: this.lineData?.equipment?.issueNotes || '',
      },
    });

    dialogRef.onClose.subscribe((result) => {
      if (result?.confirmed) {
        this.saveEquipmentIssue(result.issueReported, result.issueNotes);
      }
    });
  }

  /**
   * Update issue reported - removes a specific issue tag
   */
  updateIssueReported(event: { text: string }): void {
    const issueToRemove = event.text;
    const currentIssues = this.lineData?.equipment?.issueReported || '';
    const issuesArray = currentIssues
      .split(',')
      .filter((issue) => issue.trim() !== issueToRemove.trim());
    const newIssueReported = issuesArray.join(',');

    this.saveEquipmentIssue(
      newIssueReported,
      this.lineData?.equipment?.issueNotes || '',
    );
  }

  /**
   * Update issue notes
   */
  updateIssueNotes(newNotes: string): void {
    this.saveEquipmentIssue(
      this.lineData?.equipment?.issueReported || '',
      newNotes,
    );
  }

  /**
   * Clear all issues
   */
  clearAllIssues(): void {
    this.saveEquipmentIssue('', '');
  }

  /**
   * Save equipment issue to the backend
   */
  private saveEquipmentIssue(issueReported: string, issueNotes: string): void {
    const equipmentId =
      this.lineData?.equipment?.id || this.lineData?.equipmentId;

    if (!equipmentId) {
      this.toastrService.danger('Equipment ID not found', 'Error');
      return;
    }

    this.sub.sink = this.apollo
      .mutate({
        mutation: MUTATION_UPDATEEQUIPMENT,
        variables: {
          data: {
            id: equipmentId,
            issueReported: issueReported || null,
            issueNotes: issueNotes || null,
          },
        },
      })
      .subscribe({
        next: () => {
          // Update local state
          if (this.lineData?.equipment) {
            this.lineData.equipment.issueReported = issueReported;
            this.lineData.equipment.issueNotes = issueNotes;
          }
          this.toastrService.success('Equipment issue updated', 'Success');
        },
        error: (error) => {
          console.error('Error updating equipment issue:', error);
          this.toastrService.danger(
            error.message || 'Failed to update equipment issue',
            'Error',
          );
        },
      });
  }
}
